import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, EmptyState, Pagination } from '@ecom/ui-kit';
import { useMemo, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCategoriesQuery, useProductsQuery } from '../api';
import { ProductTable } from '../components/ProductTable';
import { ProductTableToolbar } from '../components/ProductTableToolbar';
import { applyProductTableState, DEFAULT_PRODUCT_TABLE_STATE } from '../table-utils';
import type { Category } from '../types';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';

export function ProductListPage(): ReactElement {
  const { t } = useI18n();
  const navigate = useNavigate();
  const productsQuery = useProductsQuery();
  const categoriesQuery = useCategoriesQuery();
  const [tableState, setTableState] = useState(DEFAULT_PRODUCT_TABLE_STATE);

  const categoriesById = useMemo<ReadonlyMap<string, Category>>(
    () => new Map((categoriesQuery.data ?? []).map((category) => [category.id, category])),
    [categoriesQuery.data],
  );

  const result = useMemo(
    () => applyProductTableState(productsQuery.data ?? [], tableState),
    [productsQuery.data, tableState],
  );

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="sk-page-title">{t('merchant.nav.products')}</h1>
          <p className="text-sm text-inkFaint">{t('merchant.products.subtitle')}</p>
        </div>
        <Button
          onClick={() => {
            void navigate('/products/new');
          }}
        >
          {t('merchant.products.newProduct')}
        </Button>
      </div>

      {productsQuery.isPending ? (
        <TableSkeleton columns={6} />
      ) : productsQuery.isError ? (
        <EmptyState
          title={t('merchant.products.loadError')}
          description={t(resolveErrorMessageKey(productsQuery.error))}
        />
      ) : productsQuery.data.length === 0 ? (
        <EmptyState
          title={t('merchant.products.emptyTitle')}
          description={t('merchant.products.emptyDescription')}
          action={
            <Button
              onClick={() => {
                void navigate('/products/new');
              }}
            >
              {t('merchant.products.createAction')}
            </Button>
          }
        />
      ) : (
        <>
          <ProductTableToolbar state={tableState} onChange={setTableState} />

          {result.total === 0 ? (
            <EmptyState
              title={t('merchant.products.noMatchTitle')}
              description={t('merchant.products.noMatchDescription')}
            />
          ) : (
            <>
              <ProductTable products={result.items} categoriesById={categoriesById} />
              <div className="flex justify-center">
                <Pagination
                  page={result.page}
                  pageCount={result.totalPages}
                  onPageChange={(page) => {
                    setTableState((prev) => ({ ...prev, page }));
                  }}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
